import React, { useCallback, useEffect, useState } from 'react';
import { FiArrowDown, FiArrowUp, FiEdit2, FiPlus, FiRefreshCw, FiTrash2, FiX } from 'react-icons/fi';
import { useUserAssistant } from '../hooks/useUserAssistant';
import '../styles/user-assistant.scss';

const TABS = [
    { key: 'settings', label: 'الإعدادات' },
    { key: 'faqs', label: 'الأسئلة الشائعة' },
    { key: 'conversations', label: 'المحادثات' },
    { key: 'usage', label: 'الاستخدام والتكلفة' },
];
const SOURCE_LABELS = { user: 'العميل', faq: 'إجابة محفوظة', ai: 'الذكاء الاصطناعي', order: 'طلب', system: 'النظام' };
const formatDateTime = (value) => (value ? new Date(value).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const usd = (v) => `$${Number(v || 0).toFixed(Number(v) < 1 ? 4 : 2)}`;

// ---------- settings ----------
const SettingsTab = ({ api }) => {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (api.config) setForm({ ...api.config }); }, [api.config]);
    if (!form) return <div className="ua-card placeholder">جارٍ التحميل...</div>;
    const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const save = async () => {
        setSaving(true);
        await api.saveConfig({
            enabled: form.enabled,
            orderFlowEnabled: form.orderFlowEnabled,
            welcomeMessage: form.welcomeMessage,
            knowledgeBase: form.knowledgeBase,
            supportPhone: form.supportPhone,
            supportWhatsapp: form.supportWhatsapp,
            dailyAiMessagesPerUser: Number(form.dailyAiMessagesPerUser),
            historyTurns: Number(form.historyTurns),
            maxReplyTokens: Number(form.maxReplyTokens),
            faqMatchThreshold: Number(form.faqMatchThreshold),
            chipsCount: Number(form.chipsCount),
        });
        setSaving(false);
    };
    return (
        <div className="ua-card form">
            {api.meta && !api.meta.apiKeyConfigured ? (
                <div className="warn-box">مفتاح ANTHROPIC_API_KEY غير مضبوط على الخادم: الأسئلة الشائعة ستعمل، لكن أسئلة الذكاء الاصطناعي سترد برسالة اعتذار.</div>
            ) : null}
            <div className="switches">
                <label><input type="checkbox" checked={Boolean(form.enabled)} onChange={set('enabled')} /> تفعيل المساعد الذكي للمستخدمين</label>
                <label><input type="checkbox" checked={Boolean(form.orderFlowEnabled)} onChange={set('orderFlowEnabled')} /> السماح بطلب ساحبة من داخل المحادثة</label>
            </div>
            <label>
                رسالة الترحيب
                <textarea rows={2} value={form.welcomeMessage} onChange={set('welcomeMessage')} />
            </label>
            <label>
                معلومات التطبيق (يجيب منها الذكاء الاصطناعي: الرسوم، العمولة، طريقة العمل، السياسات)
                <textarea rows={10} value={form.knowledgeBase} onChange={set('knowledgeBase')} />
                <small>اكتب الحقائق فقط، سطراً لكل معلومة. لن يجيب المساعد عن رسوم أو سياسات غير مذكورة هنا أو في الأسئلة الشائعة.</small>
            </label>
            <div className="actions start">
                <button type="button" className="btn ghost small" onClick={() => setForm((p) => ({ ...p, knowledgeBase: api.meta?.defaults?.knowledgeBase || p.knowledgeBase }))}>استعادة النص الافتراضي</button>
            </div>
            <div className="grid">
                <label>
                    رقم الدعم (اتصال)
                    <input dir="ltr" value={form.supportPhone} onChange={set('supportPhone')} placeholder="0910000000" />
                </label>
                <label>
                    رقم الدعم (واتساب)
                    <input dir="ltr" value={form.supportWhatsapp} onChange={set('supportWhatsapp')} placeholder="218910000000" />
                </label>
                <label>
                    حد أسئلة الذكاء الاصطناعي لكل مستخدم يومياً
                    <input type="number" min="0" value={form.dailyAiMessagesPerUser} onChange={set('dailyAiMessagesPerUser')} />
                    <small>0 = بدون حد. الأسئلة الشائعة لا تُحتسب.</small>
                </label>
                <label>
                    عدد الرسائل السابقة المرسلة للذكاء الاصطناعي
                    <input type="number" min="0" max="30" value={form.historyTurns} onChange={set('historyTurns')} />
                    <small>أقل = أرخص. 8 كافية غالباً.</small>
                </label>
                <label>
                    أقصى طول للرد (توكن)
                    <input type="number" min="200" max="4000" value={form.maxReplyTokens} onChange={set('maxReplyTokens')} />
                </label>
                <label>
                    حساسية مطابقة الأسئلة الشائعة
                    <input type="number" min="0.2" max="1" step="0.05" value={form.faqMatchThreshold} onChange={set('faqMatchThreshold')} />
                    <small>أقل = إجابات محفوظة أكثر (أرخص) لكن قد تُطابق أسئلة مختلفة.</small>
                </label>
                <label>
                    عدد الأسئلة الشائعة الظاهرة في المحادثة
                    <input type="number" min="0" max="30" value={form.chipsCount} onChange={set('chipsCount')} />
                </label>
            </div>
            <div className="actions">
                <button type="button" className="btn primary" disabled={saving} onClick={save}>{saving ? 'جارٍ الحفظ...' : 'حفظ'}</button>
            </div>
        </div>
    );
};

// ---------- FAQs ----------
const EMPTY_FAQ = { question: '', answer: '', keywords: '', category: '', active: true };
const FaqEditor = ({ value, onCancel, onSave }) => {
    const [faq, setFaq] = useState({ ...value, keywords: Array.isArray(value.keywords) ? value.keywords.join('، ') : (value.keywords || '') });
    const set = (k) => (e) => setFaq((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    return (
        <div className="ua-card form editor">
            <label>السؤال<input value={faq.question} onChange={set('question')} /></label>
            <label>الإجابة<textarea rows={4} value={faq.answer} onChange={set('answer')} /></label>
            <label>
                صيغ أخرى للسؤال / كلمات مفتاحية (مفصولة بفاصلة)
                <input value={faq.keywords} onChange={set('keywords')} placeholder="عمولة، رسوم التطبيق، commission" />
                <small>تساعد على مطابقة أسئلة المستخدمين بدون تكلفة. العبارات من كلمتين أو أكثر تُطابق مباشرة.</small>
            </label>
            <div className="grid">
                <label>التصنيف<input value={faq.category} onChange={set('category')} /></label>
                <label className="check"><input type="checkbox" checked={faq.active !== false} onChange={set('active')} /> ظاهر ومفعّل</label>
            </div>
            <div className="actions">
                <button type="button" className="btn ghost" onClick={onCancel}>إلغاء</button>
                <button type="button" className="btn primary" disabled={!faq.question.trim() || !faq.answer.trim()} onClick={() => onSave(faq)}>حفظ</button>
            </div>
        </div>
    );
};

const FaqsTab = ({ api, prefill, clearPrefill }) => {
    const [editing, setEditing] = useState(null);
    useEffect(() => { api.loadFaqs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (prefill) {
            setEditing({ ...EMPTY_FAQ, question: prefill });
            clearPrefill();
        }
    }, [prefill, clearPrefill]);
    const move = (index, dir) => {
        const ids = api.faqs.map((f) => f._id);
        const target = index + dir;
        if (target < 0 || target >= ids.length) return;
        [ids[index], ids[target]] = [ids[target], ids[index]];
        api.reorderFaqs(ids);
    };
    return (
        <>
            {editing ? (
                <FaqEditor value={editing} onCancel={() => setEditing(null)} onSave={async (faq) => { if (await api.saveFaq(faq)) setEditing(null); }} />
            ) : (
                <div className="toolbar">
                    <p className="muted">الأسئلة الشائعة تظهر كأزرار في المحادثة، وأي سؤال مكتوب يطابقها يُجاب من هنا مجاناً بدون ذكاء اصطناعي. الترتيب هنا هو ترتيب الظهور.</p>
                    <button type="button" className="btn primary" onClick={() => setEditing({ ...EMPTY_FAQ })}><FiPlus /> سؤال جديد</button>
                </div>
            )}
            <div className="ua-card">
                {api.faqs.length === 0 ? <p className="muted">لا توجد أسئلة</p> : api.faqs.map((f, i) => (
                    <div key={f._id} className={`faq-row ${f.active ? '' : 'off'}`}>
                        <div className="order">
                            <button type="button" onClick={() => move(i, -1)} disabled={i === 0}><FiArrowUp /></button>
                            <button type="button" onClick={() => move(i, 1)} disabled={i === api.faqs.length - 1}><FiArrowDown /></button>
                        </div>
                        <div className="body">
                            <b>{f.question}</b>
                            <p>{f.answer}</p>
                            {f.keywords?.length ? <small>كلمات: {f.keywords.join('، ')}</small> : null}
                        </div>
                        <span className="hits num" title="عدد مرات الإجابة">{f.hits || 0}</span>
                        <div className="row-actions">
                            <button type="button" className="icon" onClick={() => setEditing(f)}><FiEdit2 /></button>
                            <button type="button" className="icon danger" onClick={() => window.confirm('حذف هذا السؤال؟') && api.deleteFaq(f._id)}><FiTrash2 /></button>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

// ---------- conversations ----------
const ConversationsTab = ({ api, onMakeFaq }) => {
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState('');
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const [unanswered, setUnanswered] = useState([]);
    const load = useCallback(async (before = '') => {
        const json = await api.listConversations({ q, filter, before });
        if (json) setData((prev) => (before && prev ? { ...json, items: [...prev.items, ...json.items] } : json));
    }, [api, q, filter]);
    useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { api.listUnanswered().then((json) => setUnanswered(json?.items || [])); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const openConv = async (id) => {
        const json = await api.getConversation(id);
        if (json) setOpen(json.conversation);
    };
    return (
        <div className="conv-layout">
            <div className="ua-card">
                <form className="toolbar" onSubmit={(e) => { e.preventDefault(); load(); }}>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث برقم الهاتف أو الاسم" />
                    <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                        <option value="">كل المحادثات</option>
                        <option value="unanswered">بها أسئلة لم يُجب عنها</option>
                        <option value="notHelpful">بها تقييم سلبي</option>
                    </select>
                    <button type="submit" className="btn ghost"><FiRefreshCw /></button>
                </form>
                {!data ? <p className="muted">جارٍ التحميل...</p> : data.items.length === 0 ? <p className="muted">لا توجد محادثات</p> : data.items.map((c) => (
                    <button key={c._id} type="button" className={`conv-row ${open?._id === c._id ? 'active' : ''}`} onClick={() => openConv(c._id)}>
                        <span><b>{c.userName}</b> <span className="muted num">{c.userPhoneNumber}</span></span>
                        <span className="muted">{c.lastMessage?.slice(0, 70)}</span>
                        <span className="meta">
                            <span className="num">{formatDateTime(c.lastMessageAt)}</span>
                            <span>ذكاء: <b className="num">{c.aiCalls}</b></span>
                            <span>محفوظة: <b className="num">{c.faqAnswers}</b></span>
                            {c.unansweredCount ? <span className="tag warn">لم يُجب: {c.unansweredCount}</span> : null}
                            {c.notHelpfulCount ? <span className="tag bad">سلبي: {c.notHelpfulCount}</span> : null}
                        </span>
                    </button>
                ))}
                {data?.nextBefore ? <button type="button" className="btn ghost more" onClick={() => load(data.nextBefore)}>عرض المزيد</button> : null}
            </div>
            <div className="side">
                {open ? (
                    <div className="ua-card transcript">
                        <div className="toolbar">
                            <b>{open.userName} <span className="muted num">{open.userPhoneNumber}</span></b>
                            <button type="button" className="icon" onClick={() => setOpen(null)}><FiX /></button>
                        </div>
                        <p className="muted">التكلفة: <span className="num">{usd(open.costUsd)}</span> · رسائل ذكاء اصطناعي: <span className="num">{open.aiCalls}</span></p>
                        {open.messages.length === 0 ? <p className="muted">مسح المستخدم سجل المحادثة</p> : open.messages.map((m) => (
                            <div key={m._id} className={`msg ${m.role}`}>
                                <span className="src">{SOURCE_LABELS[m.source] || m.source} · <span className="num">{formatDateTime(m.createdAt)}</span>{m.helpful === false ? ' · 👎' : m.helpful === true ? ' · 👍' : ''}</span>
                                <p>{m.text}</p>
                                {m.role === 'user' && m.unanswered ? <button type="button" className="btn ghost small" onClick={() => onMakeFaq(m.text)}>تحويل لسؤال شائع</button> : null}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="ua-card">
                        <h4>أسئلة لم يُجب عنها مؤخراً</h4>
                        <p className="muted">أضف إجاباتها كأسئلة شائعة ليُجاب عنها مجاناً في المرة القادمة.</p>
                        {unanswered.length === 0 ? <p className="muted">لا يوجد</p> : unanswered.map((u, i) => (
                            <div key={i} className="unanswered-row">
                                <p>{u.text}</p>
                                <span className="muted num">{formatDateTime(u.createdAt)}</span>
                                <button type="button" className="btn ghost small" onClick={() => onMakeFaq(u.text)}>تحويل لسؤال شائع</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------- usage ----------
const UsageTab = ({ api }) => {
    const [days, setDays] = useState(30);
    const [stats, setStats] = useState(null);
    useEffect(() => { api.getStats(days).then(setStats); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!stats) return <div className="ua-card placeholder">جارٍ التحميل...</div>;
    const t = stats.totals;
    const maxCost = Math.max(...stats.days.map((d) => d.costUsd || 0), 0.0001);
    return (
        <div className="ua-card">
            <div className="toolbar">
                <p className="muted">النموذج: <b dir="ltr">{stats.model}</b></p>
                <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                    <option value={7}>آخر 7 أيام</option>
                    <option value={30}>آخر 30 يوماً</option>
                    <option value={90}>آخر 90 يوماً</option>
                </select>
            </div>
            <div className="kpis">
                <div><span className="label">الرسائل</span><b className="num">{t.messages}</b></div>
                <div><span className="label">أُجيبت مجاناً (محفوظة)</span><b className="num">{t.faqAnswers}</b><small className="num">{t.faqShare}%</small></div>
                <div><span className="label">ردود الذكاء الاصطناعي</span><b className="num">{t.aiCalls}</b></div>
                <div><span className="label">التكلفة التقديرية</span><b className="num">{usd(t.costUsd)}</b><small>تقديرية حسب أسعار النموذج</small></div>
                <div><span className="label">نسبة الكاش</span><b className="num">{t.cacheHitRate}%</b><small>كلما ارتفعت انخفضت التكلفة</small></div>
                <div><span className="label">طلبات من المحادثة</span><b className="num">{t.orders}</b></div>
                <div><span className="label">وصلوا للحد اليومي</span><b className="num">{t.limitHits}</b></div>
                <div><span className="label">لم يُجب عنها</span><b className="num">{t.unanswered}</b></div>
            </div>
            <h4>التكلفة اليومية</h4>
            {stats.days.length === 0 ? <p className="muted">لا توجد بيانات بعد</p> : (
                <div className="bars">
                    {stats.days.map((d) => (
                        <div key={d.day} className="bar" title={`${d.day}: ${usd(d.costUsd)} · ${d.messages} رسالة`}>
                            <div style={{ height: `${Math.max(3, Math.round(((d.costUsd || 0) / maxCost) * 100))}%` }} />
                            <span className="num">{d.day.slice(5)}</span>
                        </div>
                    ))}
                </div>
            )}
            <h4>الأسئلة الشائعة الأكثر استخداماً</h4>
            {stats.topFaqs.length === 0 ? <p className="muted">لا يوجد</p> : stats.topFaqs.map((f) => (
                <div key={f._id} className="top-row"><span>{f.question}</span><b className="num">{f.hits}</b></div>
            ))}
        </div>
    );
};

// The "مساعد المستخدمين" tab of the Smart Assistant page.
const UserAssistantPanel = () => {
    const api = useUserAssistant();
    const [tab, setTab] = useState('settings');
    const [prefill, setPrefill] = useState('');
    useEffect(() => { api.loadConfig(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const makeFaq = (question) => {
        setPrefill(question);
        setTab('faqs');
    };
    const clearPrefill = useCallback(() => setPrefill(''), []);
    return (
        <div className="ua-panel">
            <div className="ua-tabs">
                {TABS.map((t) => (
                    <button key={t.key} type="button" className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>{t.label}</button>
                ))}
                {api.config ? <span className={`status ${api.config.enabled ? 'on' : 'off'}`}>{api.config.enabled ? 'مفعّل' : 'متوقف'}</span> : null}
            </div>
            {tab === 'settings' ? <SettingsTab api={api} /> : null}
            {tab === 'faqs' ? <FaqsTab api={api} prefill={prefill} clearPrefill={clearPrefill} /> : null}
            {tab === 'conversations' ? <ConversationsTab api={api} onMakeFaq={makeFaq} /> : null}
            {tab === 'usage' ? <UsageTab api={api} /> : null}
        </div>
    );
};

export default UserAssistantPanel;
